import { forwardRef, useMemo } from "react";
import classNames from "classnames";
import PropTypes from "prop-types";
import useScreenSize from "../../../hooks/useScreenSize";
import DropdownItem from "./DropdownItem";
import RecentSavedTabs from "./RecentSavedTabs";
import NoRecentOrSaved from "./NoRecentOrSaved";
import { useSelector } from "react-redux";
import {
  selectRecentSearches,
  selectSavedSearches,
} from "../../../features/enhancedSearch";

const SearchResults = forwardRef((props, ref) => {
  const DESKTOP_LIMIT = 10;
  const MOBILE_LIMIT = 7;

  const {
    items,
    keyword,
    type,
    resultStyle,
    showSavedOrRecent = false,
    showSuggestion,
    handleSelect,
  } = props;

  const recentSearches = useSelector(selectRecentSearches);
  const savedSearches = useSelector(selectSavedSearches);
  const { isDesktop } = useScreenSize();

  const isDrugIndex = items && "flattedResults" in items;
  const searchItems = isDrugIndex ? items.flattedResults : items;

  const noRecentOrSaved =
    (recentSearches?.combinedSearches?.length ?? 0) === 0 &&
    Object.keys(savedSearches ?? {}).length === 0;

  const reducedList = useMemo(
    () => searchItems?.slice(0, isDesktop ? DESKTOP_LIMIT : MOBILE_LIMIT) || [],
    [searchItems, isDesktop],
  );

  const highlightText = (text, keyword) => {
    if (!keyword?.trim()) return text;
    const parts = text.split(new RegExp(`(${keyword})`, "gi"));
    return parts.map((part, index) =>
      part.toLowerCase() === keyword.toLowerCase() ? (
        <span key={index} className={resultStyle.highlight}>
          {part}
        </span>
      ) : (
        part
      ),
    );
  };

  return (
    <div
      ref={ref}
      aria-expanded="true"
      className={classNames({
        [resultStyle.recentAndSavedSearchDropdown]: type === "search",
        [resultStyle.fullScreen]: !isDesktop,
        "drug-index-dropdown": type !== "search",
      })}
      data-testid="search-results"
    >
      {showSavedOrRecent && !noRecentOrSaved ? (
        <RecentSavedTabs
          recentSearches={recentSearches}
          savedSearches={savedSearches}
          handleSelect={handleSelect}
          isDrugIndex={isDrugIndex}
          highlightText={highlightText}
          keyword={keyword}
          resultStyle={resultStyle}
        />
      ) : showSavedOrRecent ? (
        <NoRecentOrSaved resultStyle={resultStyle} />
      ) : null}

      {showSuggestion &&
        (reducedList.length > 0 ? (
          reducedList.map((item, index) => (
            <DropdownItem
              key={index}
              item={item}
              index={index}
              isSuggestion={true}
              handleSelect={handleSelect}
              isDrugIndex={isDrugIndex}
              highlightText={highlightText}
              keyword={keyword}
              resultStyle={resultStyle}
            />
          ))
        ) : (
          <div className={resultStyle.noResults}>
            No results found. Please try different keywords.
          </div>
        ))}
    </div>
  );
});

SearchResults.propTypes = {
  items: PropTypes.oneOfType([PropTypes.array, PropTypes.object]).isRequired,
  keyword: PropTypes.string,
  type: PropTypes.string.isRequired,
  resultStyle: PropTypes.object.isRequired,
  showSavedOrRecent: PropTypes.bool,
  showSuggestion: PropTypes.bool.isRequired,
  handleSelect: PropTypes.func.isRequired,
};

SearchResults.defaultProps = {
  showSavedOrRecent: false,
};
export default SearchResults;
